import { PageIcon } from "./PageIcon.tsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate, useRouter } from "@tanstack/react-router";
import { addressWorkspace } from "../addresses.ts";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";
import { FormattedMessage, useIntl } from "react-intl";
import { api } from "../api.ts";
import { useAnnounce } from "../components/Announcer.tsx";
import { useFocusOnNavigation } from "../components/PageTitle.tsx";
import { errorMessage } from "../errors.ts";
import { withMessages } from "../i18n.ts";
import {
  atLeast,
  createPage,
  pageQuery,
  refreshWorkspace,
  treeQuery,
  workspacesQuery,
  type PageDetail,
  type TreePage,
} from "./data.ts";

import { rememberPage, useCommands } from "./quickfind.ts";

/** Versions of the page in other languages (ADR-0074). */
/** A breadcrumb's pages beside it and inside it (ADR-0089). */
const BreadcrumbMenu = lazy(() => import("./BreadcrumbMenu.tsx"));
const PageProperties = lazy(() =>
  import("./PageProperties.tsx").then((m) => ({ default: m.PageProperties })),
);
const PageVersions = lazy(
  withMessages(() => import("./PageVersions.tsx").then((m) => ({ default: m.PageVersions }))),
);
const PublishDialog = lazy(
  withMessages(() => import("./PublishDialog.tsx").then((m) => ({ default: m.PublishDialog }))),
);
const Comments = lazy(
  withMessages(() => import("./Comments.tsx").then((m) => ({ default: m.Comments }))),
);
const ShareDialog = lazy(
  withMessages(() => import("./ShareDialog.tsx").then((m) => ({ default: m.ShareDialog }))),
);
import { PageLabel } from "./PageLabel.tsx";
import type { DeviceId } from "./devices.ts";

/** Loaded just after the page shows (startup budget, doc 05); its commands register then. */
const PageActions = lazy(() =>
  import("./PageActions.tsx").then((m) => ({ default: m.PageActions })),
);
/** The preview's devices, for the palette (their sizes load with the preview). */
const DEVICE_IDS: DeviceId[] = [
  "phone",
  "largePhone",
  "tablet",
  "laptop",
  "desktop",
  "large",
  "custom",
];
const DevicePreview = lazy(
  withMessages(() => import("./DevicePreview.tsx").then((m) => ({ default: m.DevicePreview }))),
);

const route = getRouteApi("/w/$workspaceId/p/$pageId");

const ToolPrompt = lazy(() => import("./ToolPrompt.tsx").then((m) => ({ default: m.ToolPrompt })));

const Backlinks = lazy(
  withMessages(() => import("./Backlinks.tsx").then((m) => ({ default: m.Backlinks }))),
);
const DatabaseView = lazy(
  withMessages(() =>
    import("./database/DatabaseView.tsx").then((m) => ({ default: m.DatabaseView })),
  ),
);
const ScheduleMarker = lazy(
  withMessages(() =>
    import("../components/ScheduleMarker.tsx").then((m) => ({ default: m.ScheduleMarker })),
  ),
);
const RowProperties = lazy(
  withMessages(() =>
    import("./database/RowProperties.tsx").then((m) => ({ default: m.RowProperties })),
  ),
);
const PageFollowBar = lazy(
  withMessages(() => import("./PageFollow.tsx").then((m) => ({ default: m.PageFollowBar }))),
);
/** The page's cover and its controls (ADR-0077). */
const PageCover = lazy(
  withMessages(() => import("./PageCover.tsx").then((m) => ({ default: m.PageCover }))),
);
const NoAccess = lazy(
  withMessages(() => import("./NoAccess.tsx").then((m) => ({ default: m.NoAccess }))),
);

export function PageView() {
  const { workspaceId, pageId } = route.useParams();
  const page = useQuery(pageQuery(pageId));
  // One view for the page's analytics (ADR-0063) each time it is opened.
  const opened = page.isSuccess;
  useEffect(() => {
    if (opened) void api(`/pages/${pageId}/views`, { method: "POST" }).catch(() => undefined);
  }, [opened, pageId]);
  // On a workspace address, the page's readable address in the address bar once it is known
  // (ADR-0098 stage 2): the router shows it from then on.
  const router = useRouter();
  const address = page.data?.address;
  useEffect(() => {
    if (!addressWorkspace || !address || !window.location.pathname.startsWith("/w/")) return;
    const { search, hash } = window.location;
    router.history.replace(
      `${address.split("/").map(encodeURIComponent).join("/")}${search}${hash}`,
      router.history.location.state,
    );
  }, [address, router]);

  if (page.isError) {
    // Maybe blocks of it were shared with them; or they may ask for access (ADR-0027).
    return (
      <Suspense
        fallback={
          <p aria-busy="true">
            <FormattedMessage id="app.loading" />
          </p>
        }
      >
        <NoAccess pageId={pageId} error={page.error} />
      </Suspense>
    );
  }
  if (!page.data) {
    return (
      <p aria-busy="true">
        <FormattedMessage id="app.loading" />
      </p>
    );
  }
  // Keyed by page, so local title state never carries over to another page.
  return <PageBody key={pageId} workspaceId={workspaceId} detail={page.data} />;
}

const Editor = lazy(withMessages(() => import("../editor/Editor.tsx")));

function PageBody({ workspaceId, detail }: { workspaceId: string; detail: PageDetail }) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tree = useQuery(treeQuery(workspaceId));
  const search = route.useSearch();
  const isNew = search.new === true;
  const article = useRef<HTMLElement>(null);
  const [publishing, setPublishing] = useState(false);
  // The device the page is previewed on, while it is (ADR-0058).
  const [previewing, setPreviewing] = useState<DeviceId | null>(null);
  // Opened to choose whom it is seen by (ADR-0059).
  const [choosingWho, setChoosingWho] = useState(false);
  // An access request in the inbox opens the Share dialog (ADR-0027).
  const [sharing, setSharing] = useState(
    search.share === true && detail.level === "full_access" && !detail.inTrash,
  );
  const { level, inTrash, inArchive, breadcrumbs } = detail;
  // Tools the workspace's owners approved, to offer connecting them where they work (ADR-0065).
  const hasTools =
    useQuery(workspacesQuery).data?.find((w) => w.id === workspaceId)?.hasTools === true;
  const titles = new Map(tree.data?.map((p) => [p.id, p.title]));
  // The sidebar's title follows typing; the page's own detail is read once.
  const page = { ...detail.page, title: titles.get(detail.page.id) ?? detail.page.title, level };
  // The title bar's actions, in the command palette too (ADR-0045).
  useCommands(
    "page-header",
    level === "full_access" && !inTrash
      ? [
          {
            id: "share",
            label: intl.formatMessage({ id: "command.share" }),
            keywords: ["share", "permissions", "invite", "access"],
            run: () => setSharing(true),
          },
          {
            id: "publish",
            label: intl.formatMessage({ id: "command.publish" }),
            keywords: ["publish", "site", "web", "public"],
            run: () => setPublishing(true),
          },
        ]
      : [],
  );
  // Previewed on devices by whoever may edit the page (ADR-0058).
  const canPreview = !inTrash && atLeast(level, "can_edit");
  useCommands(
    "page-preview",
    !canPreview
      ? []
      : [
          {
            id: "preview",
            label: intl.formatMessage({ id: "page.preview" }),
            keywords: ["preview", "devices", "responsive", "phone", "tablet", "screen"],
            run: () => setPreviewing("laptop"),
          },
          ...(level === "full_access"
            ? [
                {
                  id: "preview-as",
                  label: intl.formatMessage({ id: "page.previewAsSomeone" }),
                  keywords: ["preview", "as", "user", "person", "group", "role", "permissions"],
                  run: () => {
                    setChoosingWho(true);
                    setPreviewing("laptop");
                  },
                },
              ]
            : []),
          ...DEVICE_IDS.map((id) => ({
            id: `preview-${id}`,
            label: intl.formatMessage(
              { id: "page.previewAs" },
              { device: intl.formatMessage({ id: `page.device.${id}` }) },
            ),
            keywords: ["preview", "device", "screen", "width"],
            run: () => setPreviewing(id),
          })),
        ],
  );
  // After navigating here, focus starts at the page (a new page focuses its title instead).
  useFocusOnNavigation(article, !isNew);
  // Quick Find lists the pages opened last.
  useEffect(() => rememberPage(workspaceId, detail.page.id), [workspaceId, detail.page.id]);

  async function addSubpage() {
    const id = await createPage(workspaceId, { parentId: page.id });
    await refreshWorkspace(queryClient, workspaceId);
    await navigate({
      to: "/w/$workspaceId/p/$pageId",
      params: { workspaceId, pageId: id },
      search: { new: true },
    });
  }

  return (
    <article
      ref={article}
      tabIndex={-1}
      className={detail.fullWidth ? "page page-full" : "page"}
      aria-label={page.title || intl.formatMessage({ id: "page.untitled" })}
    >
      <div className="page-toolbar">
        {breadcrumbs.length > 0 ? (
          <nav aria-label={intl.formatMessage({ id: "page.breadcrumbs" })} className="breadcrumbs">
            <ol>
              {breadcrumbs.map((b) => (
                <li key={b.id}>
                  <Link to="/w/$workspaceId/p/$pageId" params={{ workspaceId, pageId: b.id }}>
                    {/* The sidebar's titles update as people type; the stored ones a moment later. */}
                    <PageLabel title={titles.get(b.id) ?? b.title} icon={b.icon} />
                  </Link>
                  {tree.data && (
                    <Suspense fallback={null}>
                      <BreadcrumbMenu workspaceId={workspaceId} crumb={b} pages={tree.data} />
                    </Suspense>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        ) : (
          <span />
        )}
        {!inTrash && tree.data && (
          <div className="page-toolbar-actions">
            {level === "full_access" && (
              <>
                <Button className="button" onPress={() => setSharing(true)}>
                  <FormattedMessage id="share.button" />
                </Button>
                <Button className="button" onPress={() => setPublishing(true)}>
                  <FormattedMessage id="publish.button" />
                </Button>
              </>
            )}
            {canPreview && (
              <Button className="button" onPress={() => setPreviewing("laptop")}>
                <FormattedMessage id="page.preview" />
              </Button>
            )}
            <Suspense fallback={null}>
              <PageFollowBar pageId={page.id} workspaceId={workspaceId} />
            </Suspense>
            <Suspense fallback={null}>
              <PageActions
                workspaceId={workspaceId}
                page={page}
                pages={tree.data}
                onAddSubpage={() => void addSubpage()}
                variant="labelled"
                locked={detail.locked}
                fullWidth={detail.fullWidth}
                hasCover={detail.page.cover !== null}
                hasIcon={page.icon !== null}
                isTemplate={detail.page.isTemplate ?? false}
              />
            </Suspense>
          </div>
        )}
        {publishing && (
          <Suspense fallback={null}>
            <PublishDialog
              pageId={page.id}
              title={page.title}
              onClose={() => setPublishing(false)}
            />
          </Suspense>
        )}
        {previewing && (
          <Suspense fallback={null}>
            <DevicePreview
              pageId={page.id}
              device={previewing}
              onDevice={setPreviewing}
              onClose={() => {
                setPreviewing(null);
                setChoosingWho(false);
              }}
              canPreviewAs={level === "full_access"}
              chooseWho={choosingWho}
            />
          </Suspense>
        )}
        {sharing && (
          <Suspense fallback={null}>
            <ShareDialog pageId={page.id} onClose={() => setSharing(false)} />
          </Suspense>
        )}
      </div>
      {inTrash && <TrashBanner workspaceId={workspaceId} page={page} detail={detail} />}
      {!inTrash && detail.locked && (
        <p className="status lock-banner">
          <span aria-hidden="true">🔒 </span>
          <FormattedMessage id="page.lockedBanner" />
        </p>
      )}
      {!inTrash && detail.page.kind !== "row" && (
        <Suspense fallback={null}>
          <PageVersions workspaceId={workspaceId} pageId={page.id} title={page.title} />
        </Suspense>
      )}
      {!inTrash && atLeast(detail.level, "full_access") && (
        <Suspense fallback={null}>
          <ScheduleMarker pageId={page.id} />
        </Suspense>
      )}
      {!inTrash && inArchive && (
        <ArchiveBanner workspaceId={workspaceId} page={page} detail={detail} />
      )}
      <Suspense fallback={null}>
        <Backlinks workspaceId={workspaceId} pageId={page.id} />
      </Suspense>
      {!inTrash && hasTools && (
        <Suspense fallback={null}>
          <ToolPrompt pageId={page.id} />
        </Suspense>
      )}
      <Suspense
        fallback={
          page.icon ? (
            <span className="page-icon-large" aria-hidden="true">
              <PageIcon icon={page.icon} />
            </span>
          ) : null
        }
      >
        <PageCover
          pageId={page.id}
          workspaceId={workspaceId}
          cover={detail.page.cover}
          icon={page.icon}
          canEdit={!inTrash && !detail.locked && atLeast(level, "can_edit")}
          properties={detail.page.kind === "page"}
        />
      </Suspense>
      {/* Properties on any page (ADR-0087), under its title; loaded with the page. */}
      {!inTrash && detail.page.kind === "page" && (
        <Suspense fallback={null}>
          <PageProperties pageId={page.id} workspaceId={workspaceId} title={page.title} />
        </Suspense>
      )}
      {detail.page.kind === "row" && (
        <Suspense fallback={null}>
          <RowProperties rowId={page.id} workspaceId={workspaceId} />
        </Suspense>
      )}
      <Suspense
        fallback={
          <p aria-busy="true">
            <FormattedMessage id="app.loading" />
          </p>
        }
      >
        <Editor
          workspaceId={workspaceId}
          pageId={page.id}
          focusTitle={isNew}
          pageHref={(id) => `/w/${workspaceId}/p/${id}`}
          icon={page.icon}
        />
      </Suspense>
      {detail.page.kind === "database" && (
        <Suspense
          fallback={
            <p aria-busy="true">
              <FormattedMessage id="app.loading" />
            </p>
          }
        >
          <DatabaseView workspaceId={workspaceId} databaseId={page.id} />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <Comments pageId={page.id} />
      </Suspense>
    </article>
  );
}

/** An archived page stays readable and editable; whoever may edit it can unarchive it (ADR-0025). */
function ArchiveBanner(props: { workspaceId: string; page: TreePage; detail: PageDetail }) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const announce = useAnnounce();
  const here = props.detail.archived;

  async function unarchive() {
    try {
      await api(`/pages/${props.page.id}/unarchive`, { method: "POST" });
      await refreshWorkspace(queryClient, props.workspaceId);
      announce(intl.formatMessage({ id: "page.unarchived" }));
    } catch (err) {
      announce(errorMessage(intl, err));
    }
  }

  useCommands(
    "page-unarchive",
    here && atLeast(props.page.level, "can_edit")
      ? [
          {
            id: "unarchive",
            label: intl.formatMessage({ id: "page.unarchive" }),
            keywords: ["archive", "unarchive", "restore"],
            run: () => void unarchive(),
          },
        ]
      : [],
  );
  return (
    <div
      className="status trash-banner"
      role="region"
      aria-label={intl.formatMessage({ id: "page.archivedBanner" })}
    >
      <p>
        <FormattedMessage id={here ? "page.archivedBanner" : "page.archivedAncestor"} />
      </p>
      {here && atLeast(props.page.level, "can_edit") && (
        <Button className="button" onPress={() => void unarchive()}>
          <FormattedMessage id="page.unarchive" />
        </Button>
      )}
    </div>
  );
}

function TrashBanner(props: { workspaceId: string; page: TreePage; detail: PageDetail }) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const announce = useAnnounce();
  // Only the page that was deleted can be restored; its sub-pages come back with it.
  const deletedHere = props.detail.deleted;

  async function restore() {
    try {
      await api(`/pages/${props.page.id}/restore`, { method: "POST" });
      await refreshWorkspace(queryClient, props.workspaceId);
      announce(intl.formatMessage({ id: "trash.restored" }));
    } catch (err) {
      announce(errorMessage(intl, err));
    }
  }

  useCommands(
    "page-restore",
    deletedHere
      ? [
          {
            id: "restore",
            label: intl.formatMessage({ id: "trash.restore" }),
            keywords: ["trash", "restore", "undelete"],
            run: () => void restore(),
          },
        ]
      : [],
  );
  return (
    <div
      className="status trash-banner"
      role="region"
      aria-label={intl.formatMessage({ id: "trash.banner" })}
    >
      <p>
        <FormattedMessage id={deletedHere ? "trash.banner" : "trash.bannerAncestor"} />
      </p>
      {deletedHere && (
        <Button className="button" onPress={() => void restore()}>
          <FormattedMessage id="trash.restore" />
        </Button>
      )}
    </div>
  );
}
